import { BarangayTabulation, PollResponse, PrecinctTabulation, ProvincialDistrictTabulation, TabulationTally } from "../../types";
import { Button, Grid, LinearProgress, Typography } from "@mui/material";
import { collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";

import { db } from "../../firebase";
import { useState } from "react";

const BuildDapaDashboard: React.FC = () => {
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [barangayTabulations, setBarangayTabulations] = useState<BarangayTabulation[]>([]);
  const [precinctTabulations, setPrecinctTabulations] = useState<PrecinctTabulation[]>([]);
  const [voterNames, setVoterNames] = useState<string[]>([]);

  const [tabulatedBarangays, setTabulatedBarangays] = useState<number>(0);
  const [tabulatedPrecincts, setTabulatedPrecincts] = useState<number>(0);
  const [processedVoters, setProcessedVoters] = useState<number>(0);

  const baseTabulationPath = '/tabulationsV2/SURIGAO DEL NORTE_DISTRICT 1/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts/DISTRICT 1/cities/DAPA';
  const targetTabulationPath = '/tabulationsV2/SURIGAO DEL NORTE_DAPA/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts/DISTRICT 1/cities/DAPA';

  const processVoter = async (voterName: string, precinct: PrecinctTabulation) => {
    const allVoterResponsesRef = collection(db, `${baseTabulationPath}/barangays/${precinct.barangay}/precincts/${precinct.precinct}/voters/${voterName}/pollResponses`);
    const q = query(allVoterResponsesRef, orderBy('submittedAt', 'asc'));
    const allVoterResponsesSnap = await getDocs(q);

    let latestPollResponse = null;
    for (const responseDoc of allVoterResponsesSnap.docs) {
      const response = responseDoc.data() as PollResponse;
      if ((response.responses['DISTRICT 1 REPRESENTATIVE'] === 'Lucille Sering') && 
        (response.responses['MAYOR'] !== 'Peter Boy Ruaya')
      ) {
        // do not include if rep is Lucille Sering and mayor is not Peter Boy Ruaya
        continue;
      }
      latestPollResponse = response;
      await setDoc(doc(db, `${targetTabulationPath}/barangays/${precinct.barangay}/precincts/${precinct.precinct}/voters/${voterName}/pollResponses/${response.id}`), latestPollResponse);
    }
    
    if (latestPollResponse) {
      await setDoc(doc(db, `${targetTabulationPath}/barangays/${precinct.barangay}/precincts/${precinct.precinct}/voters/${voterName}`), latestPollResponse);
    }
  };

  const getVoterNames = async (precinct: PrecinctTabulation) => {
    const collectionRef = collection(db, `${baseTabulationPath}/barangays/${precinct.barangay}/precincts/${precinct.id}/voters`);
    const collectionSnap = await getDocs(collectionRef);
    return collectionSnap.docs.map((doc) => doc.id);
  };

  const processPrecinct = async (precinct: PrecinctTabulation) => {
    const voterNames = await getVoterNames(precinct);
    setVoterNames(voterNames);

    setProcessedVoters(0);
    for (const name of voterNames) {
      await processVoter(name, precinct);
      setProcessedVoters((prev) => prev + 1);
    }

    const tally: TabulationTally = {};
    
    // tabulate precinct
    const voterResponsesColRef = collection(db, `${targetTabulationPath}/barangays/${precinct.barangay}/precincts/${precinct.id}/voters`);
    const voterResponsesColSnap = await getDocs(voterResponsesColRef);
    for (const voterResponseDoc of voterResponsesColSnap.docs) {
      const response = voterResponseDoc.data() as PollResponse;

      for (const [key, value] of Object.entries(response.responses)) {
        if (value) {
          if (key === 'VOTER') {  // exclude voter name
            continue;
          }

          // Initialize the question in tally if not exists
          if (!tally[key]) {
            tally[key] = {};
          }
          
          // Increment the count for this specific answer
          const answerValue = String(value);
          if (answerValue) {
            tally[key][answerValue] = (tally[key][answerValue] || 0) + 1;
          }
        }
      }
    }

    const targetPrecinctTab = doc(db, `${targetTabulationPath}/barangays/${precinct.barangay}/precincts/${precinct.id}`);
    await setDoc(targetPrecinctTab, {
      ...precinct,
      tally,
      updatedAt: serverTimestamp(),
    }, { merge: false });
  };

  const getPrecincts = async (barangay: BarangayTabulation) => {
    const precinctsColRef = collection(db, `${baseTabulationPath}/barangays/${barangay.id}/precincts`);
    const precinctsSnap = await getDocs(precinctsColRef);
    return precinctsSnap.docs.map((doc) => doc.data() as PrecinctTabulation);
  };

  const processBarangay = async (barangay: BarangayTabulation) => {
    const precincts = await getPrecincts(barangay);
    setPrecinctTabulations(precincts);

    setTabulatedPrecincts(0);
    for (const precinct of precincts) {
      await processPrecinct(precinct);
      setTabulatedPrecincts((prev) => prev + 1);
    }

    // sum-up precincts
    const targetPrecinctsColRef = collection(db, `${targetTabulationPath}/barangays/${barangay.id}/precincts`);
    const targetPrecinctsSnap = await getDocs(targetPrecinctsColRef);
    const targetPrecincts = targetPrecinctsSnap.docs.map((doc) => doc.data() as PrecinctTabulation);
    const sumUpPrecincts = targetPrecincts.reduce((acc, precinct) => {
      for (const [key, value] of Object.entries(precinct.tally)) {
        if (!acc[key]) {
          acc[key] = {};
        }

        for (const [answer, count] of Object.entries(value)) {
          acc[key][answer] = (acc[key][answer] || 0) + count;
        }
      }

      return acc;
    }, {} as TabulationTally);

    const targetBarangayTab = doc(db, `${targetTabulationPath}/barangays/${barangay.id}`);
    await setDoc(targetBarangayTab, {
      ...barangay,
      tally: sumUpPrecincts,
      updatedAt: serverTimestamp(),
    }, { merge: false });
  } 

  const getBarangays = async () => {
    const collectionRef = collection(db, baseTabulationPath, 'barangays');
    const collectionSnap = await getDocs(collectionRef);
    return collectionSnap.docs.map((doc) => doc.data() as BarangayTabulation);
  };

  const handleTabulate = async () => {
    setBuilding(true);
    setError(null);

    const barangays = await getBarangays();
    setBarangayTabulations(barangays);
    setTabulatedBarangays(0);

    for (const barangay of barangays) {
      await processBarangay(barangay);
      setTabulatedBarangays((prev) => prev + 1);
    }

    // sum-up barangays
    const targetBarangaysColRef = collection(db, `${targetTabulationPath}/barangays`);
    const targetBarangaysSnap = await getDocs(targetBarangaysColRef);
    const targetBarangays = targetBarangaysSnap.docs.map((doc) => doc.data() as BarangayTabulation);
    const sumUpBarangays = targetBarangays.reduce((acc, barangay) => {
      for (const [key, value] of Object.entries(barangay.tally)) {
        if (!acc[key]) {
          acc[key] = {};
        }

        for (const [answer, count] of Object.entries(value)) {
          acc[key][answer] = (acc[key][answer] || 0) + count;
        }
      }

      return acc;
    }, {} as TabulationTally);

    const baseTabulation = doc(db, baseTabulationPath);
    const baseTabulationSnap = await getDoc(baseTabulation);
    const baseTabulationData = baseTabulationSnap.data() as ProvincialDistrictTabulation;
    const targetTabulation = doc(db, targetTabulationPath);
    await setDoc(targetTabulation, {
      ...baseTabulationData,
      tally: sumUpBarangays,
      updatedAt: serverTimestamp(),
    }, { merge: false });
  };

  return (
    <Grid
      container
      direction="column"
      sx={{
        height: '100vh',
      }}
    >
      <Grid item sx={{ mt: 2 }}>
        <Typography variant="h4" gutterBottom>
          Build DAPA Dashboard
        </Typography>
      </Grid>
      <Button
        variant="contained"
        onClick={handleTabulate}
        disabled={building}
      >
        {building ? 'Building...' : 'Build'}
      </Button>
      {error && (
        <Typography variant="body1" color="error" gutterBottom>
          {error}
        </Typography>
      )}
      {barangayTabulations.length > 0 && (
        <Grid item sx={{ mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={(tabulatedBarangays / barangayTabulations.length) * 100} 
          />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            {`${tabulatedBarangays} / ${barangayTabulations.length} Barangays Processed`}
          </Typography>
        </Grid>
      )}
      {precinctTabulations.length > 0 && (
        <Grid item sx={{ mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={(tabulatedPrecincts / precinctTabulations.length) * 100} 
          />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            {`${tabulatedPrecincts} / ${precinctTabulations.length} Precincts Processed`}
          </Typography>
        </Grid>
      )}
      {voterNames.length > 0 && (
        <Grid item sx={{ mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={(processedVoters / voterNames.length) * 100} 
          />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            {`${processedVoters} / ${voterNames.length} Voters Processed`}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default BuildDapaDashboard;
